// app/features/blog/components/BlogArticleComponent.tsx
'use client';

import Link from 'next/link';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { BlogArticle } from '../blog.interface';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

// Editorial content often starts with a `# Title` line that duplicates the page h1.
// We also strip the first paragraph if it matches the article summary, to keep the
// body clean.
function normalizeMarkdown(raw: string, title?: string, summary?: string | null): string {
  let text = raw.replace(/^\uFEFF/, '').trimStart();

  // Drop a leading top-level heading (duplicate of the page h1)
  text = text.replace(/^#\s+.+\n+/, '');

  // Drop a leading paragraph that matches the article summary verbatim
  if (summary) {
    const trimmedSummary = summary.trim();
    if (trimmedSummary && text.startsWith(trimmedSummary)) {
      text = text.slice(trimmedSummary.length).trimStart();
    }
  }

  // Defensive: if the remaining top line is literally the article title (no `#`),
  // drop it too.
  if (title) {
    const firstLineEnd = text.indexOf('\n');
    const firstLine = firstLineEnd === -1 ? text : text.slice(0, firstLineEnd);
    if (firstLine.trim() === title.trim()) {
      text = firstLineEnd === -1 ? '' : text.slice(firstLineEnd + 1).trimStart();
    }
  }

  return text;
}

interface BlogArticleComponentProps {
  article: BlogArticle;
}

export default function BlogArticleComponent({ article }: BlogArticleComponentProps) {
  const normalized = normalizeMarkdown(
    article.content_markdown || '',
    article.title,
    article.summary,
  );

  return (
    <div>
      <div
        className={[
          'prose prose-xl max-w-none',
          // Headings
          'prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-gray-900',
          'prose-h2:text-3xl prose-h2:font-bold prose-h2:mt-14 prose-h2:mb-5',
          'prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-10 prose-h3:mb-4',
          'prose-h4:text-xl prose-h4:font-semibold prose-h4:mt-8 prose-h4:mb-3',
          // Body
          'prose-p:text-gray-700 prose-p:leading-[1.8] prose-p:text-lg',
          'prose-li:text-gray-700 prose-li:text-lg prose-li:leading-[1.8]',
          'prose-strong:text-gray-900',
          // Links
          'prose-a:text-teal-dark prose-a:no-underline hover:prose-a:underline prose-a:font-medium',
          // Code
          'prose-code:text-teal-dark prose-code:bg-beige-light prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:font-medium prose-code:before:content-none prose-code:after:content-none',
          'prose-pre:bg-black/90 prose-pre:text-beige-light prose-pre:rounded-xl',
          // Lists
          'prose-ul:my-6 prose-ol:my-6',
          // hr
          'prose-hr:border-beige-dark prose-hr:my-14',
        ].join(' ')}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Safety net: if any stray h1 remains in the body, demote to h2
            h1: ({ children }) => {
              const text = String(children);
              const id = slugify(text);
              return (
                <h2 id={id} className="scroll-mt-24">
                  {children}
                </h2>
              );
            },
            h2: ({ children }) => {
              const text = String(children);
              const id = slugify(text);
              return (
                <h2 id={id} className="scroll-mt-24">
                  {children}
                </h2>
              );
            },
            h3: ({ children }) => {
              const text = String(children);
              const id = slugify(text);
              return (
                <h3 id={id} className="scroll-mt-24">
                  {children}
                </h3>
              );
            },
            blockquote: ({ children }) => (
              <blockquote
                className="not-prose my-10 rounded-xl bg-beige-light border-l-4 border-teal-dark px-6 py-5 text-gray-800"
              >
                <div className="space-y-3 text-lg leading-relaxed italic">
                  {children}
                </div>
              </blockquote>
            ),
            img: ({ src, alt }) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={src}
                alt={alt || ''}
                loading="lazy"
                className="rounded-xl shadow-sm"
              />
            ),
            a: ({ href, children }) => {
              if (!href) return <span>{children}</span>;

              const isExternal = href.startsWith('http') || href.startsWith('//');
              if (isExternal) {
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                );
              }

              return <Link href={href}>{children}</Link>;
            },
            table: ({ children }) => (
              <div className="not-prose my-10 overflow-x-auto rounded-xl border border-beige-dark">
                <table className="w-full text-sm">{children}</table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-beige-light">{children}</thead>
            ),
            th: ({ children }) => (
              <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b border-beige-dark">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-4 py-3 text-gray-700 border-b border-beige-dark/50">
                {children}
              </td>
            ),
          }}
        >
          {normalized}
        </ReactMarkdown>
      </div>

      {article.content?.faq && article.content.faq.length > 0 && (
        <section className="mt-20 pt-12 border-t border-beige-dark">
          <h2
            id={slugify('Frequently Asked Questions')}
            className="font-heading text-3xl font-bold tracking-tight mb-8 scroll-mt-24 text-gray-900"
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {article.content.faq.map((item, index) => (
              <div key={index} className="border-b border-beige-dark/60 pb-6 last:border-0">
                <h3 className="font-heading text-xl font-semibold mb-3 text-gray-900">
                  {item.question}
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
